import { supabase } from '../supabaseClient'

interface SortOptions {
  sortField: string
  sortDirection: 'asc' | 'desc'
}

class TripService {
  getPaginatedTrips = async (
    page: number,
    term?: string,
    filterPastTrips: boolean = true,
    sortOptions?: { sortField: string; sortDirection: 'asc' | 'desc' },
  ): Promise<any> => {
    try {
      const rangeStart = (page - 1) * 5
      const rangeEnd = page * 5 - 1

      let query = supabase
        .from('trips')
        .select(`*, simple_profile!trips_created_by_fkey (first_name, email)`, { count: 'exact' })
        .range(rangeStart, rangeEnd)

      if (term) {
        query = query.ilike('trip_name', `%${term}%`)
      }

      if (filterPastTrips) {
        query = query.gte('end_date', new Date().toISOString())
      }

      if (sortOptions?.sortField && sortOptions.sortDirection) {
        query = query.order(sortOptions.sortField, {
          ascending: sortOptions.sortDirection === 'asc',
        })
      }

      const { data, count, error } = await query

      if (error) {
        console.error('Error fetching paginated trips:', error)
        return { data: [], count: 0, totalPages: 0 }
      }

      return {
        data: data || [],
        count: count || 0,
        totalPages: Math.ceil((count || 0) / 5),
      }
    } catch (error) {
      console.error('Error in paginated trips:', error)
      return { data: [], count: 0, totalPages: 0 }
    }
  }

  countTrips = async (): Promise<any> => {
    try {
      const { data, error } = await supabase.from('trips').select('id')

      if (error) {
        console.error('Error fetching total trip count:', error)
      } else {
        return data?.length
      }
    } catch (e) {
      console.error('Error counting trips:', e)
    }
  }

  getTripByUUID = async (uuid: string): Promise<any> => {
    try {
      const { data, error } = await supabase
        .from('trips')
        .select(
          '*, profiles!trips_created_by_fkey(first_name, last_name, email, phone), locations!trips_location_uuid_fkey(uuid, latitude, longitude, name, address)',
        )
        .eq('uuid', uuid)
        .single()
      if (error) {
        console.error('Error fetching trip by uuid:', error)
      }

      return data
    } catch (error) {
      console.error('Error fetching trip by uuid:', { error })
    }
  }

  getActivitiesByTripUUID = async (tripUUID: string, sortOptions?: SortOptions): Promise<any> => {
    try {
      let query = supabase
        .from('activities')
        .select('*, locations!activities_location_uuid_fkey(uuid, latitude, longitude, name, address)')
        .eq('trip_uuid', tripUUID)
        .eq('deleted', false)

      if (sortOptions?.sortField && sortOptions.sortDirection) {
        query = query.order(sortOptions.sortField, {
          ascending: sortOptions.sortDirection === 'asc',
        })
      }

      const { data, error } = await query

      if (error) {
        console.error('Error fetching activities by trip UUID:', error)
        return { data: [] }
      }

      return { data }
    } catch (error) {
      console.error('Error fetching activities:', error)
      return { data: [] }
    }
  }

  getTripUsersByTripUUID = async (tripUUID: string): Promise<any> => {
    try {
      const { data, error } = await supabase
        .from('trip_users')
        .select('user_uuid')
        .eq('trip_uuid', tripUUID)
        .eq('deleted', false)

      if (error) {
        console.error('Error fetching trip users by trip UUID:', error)
        return { data: [], error }
      }

      return { data: data || [], error }
    } catch (error) {
      console.error('Error fetching trip users:', error)
      return { data: [], error }
    }
  }

  getAccommodationsByTripUUID = async (tripUUID: string): Promise<any> => {
    try {
      const { data, error } = await supabase
        .from('accommodations')
        .select('*, locations!accommodations_location_uuid_fkey(uuid, latitude, longitude, name, address)')
        .eq('trip_uuid', tripUUID)
        .eq('deleted', false)

      if (error) {
        console.error('Error fetching accommodations by trip UUID:', error)
        return { data: [], error }
      }

      return { data: data || [], error }
    } catch (error) {
      console.error('Error fetching accommodations:', error)
      return { data: [], error }
    }
  }

  deleteTrip = async (tripId: any, profileUUID: any) => {
    const { error } = await supabase
      .from('trips')
      .update({
        deleted: true,
        deleted_on: new Date().toISOString(),
        deleted_by: profileUUID,
      })
      .eq('id', tripId)

    if (error) {
      throw error
    }
  }

  updateTrip = async (
    uuid: string,
    payload: {
      trip_name?: string
      location?: string
      location_uuid?: string
      start_date?: string
      end_date?: string
      updated_by: string
    },
  ) => {
    const { error } = await supabase
      .from('trips')
      .update({
        ...payload,
        updated_on: new Date().toISOString(),
      })
      .eq('uuid', uuid)

    if (error) {
      throw error
    }
  }
}

const tripService = new TripService()

export default tripService
